import useFieldTypes from "api/api-hooks/cloud/useFieldTypes";
import useToggle from "hooks/useToggle";
import { useMemo, useState } from "react";
import { Badge, Button } from "reactstrap";
import FieldUtils from "utils/FieldUtils";
import { callFunctionIfExists } from "utils/GeneralUtils";
import CreateFieldDrawer from "../CreateFieldDrawer";
import EditFieldDrawer from "../EditFieldDrawer";
import _ from "underscore";
import AppNavbar from "components/AppNavbar/AppNavbar";
import CustomToggle from "../CustomToggle";
import SubitemRecordName from "./SubitemRecordName";

// Used for configuring Subitem fields inside CreateFieldDrawer/EditFieldDrawer.
const FieldTypeSubitem = ({
  fieldValues,
  setFieldValues,
  allowFieldTypeEdit,
}) => {
  const [createDrawer, toggleCreateDrawer] = useToggle();
  const [editField, setEditField] = useState(null);
  const [activeTab, setActiveTab] = useState("Fields");

  const createSubitemField = (subitemValues, onSuccess) => {
    const newSubitemFields = createField(
      subitemValues,
      fieldValues.subitemFields
    );

    setFieldValues((prevFieldValues) => {
      const newFieldValues = { ...prevFieldValues };
      newFieldValues.subitemFields = newSubitemFields;
      return newFieldValues;
    });

    callFunctionIfExists(onSuccess);
  };

  const modifySubitemField = (APIName, subitemValues, onSuccess) => {
    const formattedDefaultVal = FieldUtils.getFormattedDefaultValue(
      subitemValues.defaultValue,
      subitemValues.fieldType
    );
    const type = subitemValues.fieldType;

    const payload = {
      Name: subitemValues.name,
      APIName: APIName,
      Description: subitemValues.description ? subitemValues.description : "",
      FieldType: type,
      DefaultValue: formattedDefaultVal,
      ...((type === "SingleReference" || type === "MultiReference") &&
        subitemValues.defaultSource && {
          DefaultSource: subitemValues.defaultSource,
        }),
      Required: subitemValues.required ? true : false,
      Unique: subitemValues.unique ? true : false,
      SourcesList: subitemValues.source ? subitemValues.source : null,
      ...(type === "Toggle" && {
        TrueDisplayName: subitemValues.trueDisplayName,
      }),
      ...(type === "Toggle" && {
        FalseDisplayName: subitemValues.falseDisplayName,
      }),
      ReadOnly: subitemValues.readonly,
      Sensitive: subitemValues.sensitive,
      RecordSummary: subitemValues.summary,
      RecordSummaryFields: subitemValues.summaryFields,
      ...(type === "Image" && { Presentation: subitemValues.presentation }),
      ...((type === "Image" ||
        type === "SingleFile" ||
        type === "Audio" ||
        type === "Video") && { Public: subitemValues.public }),
      SubitemParent: subitemValues.subitemParent,
    };

    setFieldValues((prevFieldValues) => {
      const newFieldValues = { ...prevFieldValues };

      const foundIndex = _.findIndex(newFieldValues.subitemFields, {
        APIName: APIName,
      });

      if (foundIndex < -1) {
        return console.error("Subitem failed to update. Subitem not found.");
      }

      newFieldValues.subitemFields[foundIndex] = payload;
      return newFieldValues;
    });
    callFunctionIfExists(onSuccess);
  };

  const deleteSubitemField = (apiName) => {
    setFieldValues((prevFieldValues) => {
      const newFieldValues = { ...prevFieldValues };
      newFieldValues.subitemFields = newFieldValues.subitemFields.filter(
        (field) => field.APIName !== apiName
      );
      return newFieldValues;
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;

    setFieldValues((prev) => ({ ...prev, [name]: checked }));
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-start mt--2">
        <div style={{ width: 300 }} className="mb-3">
          <AppNavbar
            tabs={[
              { title: "Subitem Fields", activeValue: "Fields" },
              { title: "Display Settings", activeValue: "Settings" },
            ]}
            activeTab={activeTab}
            onSelect={(tab) => setActiveTab(tab)}
          />
        </div>
        {activeTab === "Fields" && (
          <Button
            size="sm"
            color="primary"
            onClick={toggleCreateDrawer}
            style={{ width: 100, height: 33, marginTop: 4 }}
          >
            Add Field
          </Button>
        )}
      </div>
      {activeTab === "Fields" && (
        <FieldsList
          fields={fieldValues?.subitemFields}
          onEdit={(f) => setEditField(f)}
          onDelete={(f) => deleteSubitemField(f.APIName)}
          emptyMessage="Add your first subitem field to get started."
        />
      )}
      {activeTab === "Settings" && (
        <>
          <SubitemRecordName
            fieldValues={fieldValues}
            setFieldValues={setFieldValues}
          />
        </>
      )}
      <CreateFieldDrawer
        open={createDrawer}
        onClose={toggleCreateDrawer}
        onSave={createSubitemField}
        size="sm"
        disableSubitem
        subitemParent={fieldValues.apiName}
      />
      <EditFieldDrawer
        open={editField !== null}
        onClose={() => setEditField(null)}
        field={editField}
        onSave={modifySubitemField}
        size="sm"
        subitemParent={fieldValues.apiName}
        allowFieldTypeEdit={allowFieldTypeEdit}
      />
    </>
  );
};

export default FieldTypeSubitem;

export function createField(values, currentValues) {
  const formattedDefaultVal = FieldUtils.getFormattedDefaultValue(
    values.defaultValue,
    values.fieldType
  );
  const type = values.fieldType;

  const payload = {
    Name: values.name,
    APIName: values.apiName,
    Description: values.description ? values.description : "",
    FieldType: type,
    DefaultValue: formattedDefaultVal ?? null,
    ...((type === "SingleReference" || type === "MultiReference") &&
      values.defaultSource && {
        DefaultSource: values.defaultSource,
      }),
    Required: values.required ? true : false,
    Unique: values.unique ? true : false,
    SourcesList: values.source ? values.source : null,
    ...(type === "Toggle" && {
      TrueDisplayName: values.trueDisplayName,
    }),
    ...(type === "Toggle" && {
      FalseDisplayName: values.falseDisplayName,
    }),
    ...(type === "Image" && { Presentation: values.presentation }),
    ...((type === "Image" ||
      type === "SingleFile" ||
      type === "Audio" ||
      type === "Video") && { Public: values.public }),
    ...(type === "URL" && { GcWebsite: values.gcWebsite }),
    ReadOnly: values.readonly,
    Sensitive: values.sensitive,
    RecordSummary: values.summary,
    RecordSummaryFields: values.summaryFields,
    SubitemParent: values.subitemParent,
  };

  return [...currentValues, payload];
}

function useFieldTypesMap() {
  const { fieldTypes } = useFieldTypes();

  // Map for getting field type display name.
  const fieldTypesMap = useMemo(() => {
    const map = {};

    if (!fieldTypes) return map;

    fieldTypes.forEach((fieldType) => {
      map[fieldType.APIName] = fieldType.Name;
    });

    return map;
  }, [fieldTypes]);

  return fieldTypesMap;
}

export function FieldsList({ fields, onEdit, onDelete, emptyMessage }) {
  const fieldTypesMap = useFieldTypesMap();

  return (
    <>
      {fields?.length > 0 ? (
        fields.map((field) => (
          <div
            className="d-flex justify-content-between align-items-center bg-ultralight p-2 border rounded my-2"
            key={field?.APIName}
          >
            <div className="ml-3">
              <span className="font-weight-600">{field?.Name}</span>
              <br />
              {field?.APIName}
            </div>
            <div>
              <Badge className="mr-3">{fieldTypesMap[field?.FieldType]}</Badge>
              <Button
                className="btn-icon-only"
                size="sm"
                outline
                onClick={() => onEdit(field)}
              >
                <i className="mdi mdi-pencil" />
              </Button>
              <Button
                className="btn-icon-only"
                size="sm"
                outline
                color="danger"
                onClick={() => onDelete(field)}
              >
                <i className="mdi mdi-close" />
              </Button>
            </div>
          </div>
        ))
      ) : (
        <div className="font-weight-500 border rounded bg-ultralight p-2 mt-3 text-dark">
          {emptyMessage}
        </div>
      )}
    </>
  );
}
